import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import isKiosk from "../../_services/kiosk/kiosk.service";
import { isPublicPreloginPath } from "../../_services/public-routes";

const SessionExpiredModal = () => {
  const history = useHistory();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [dismissible, setDismissible] = useState(false);
  const showRef = useRef(false);
  const dismissedRef = useRef(false);
  const locationRef = useRef(location);
  const returnPathRef = useRef("/");

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    const handler = () => {
      if (showRef.current) return;

      const currentPath =
        locationRef.current.pathname + locationRef.current.search;
      const pathname = locationRef.current.pathname;

      // Skip kiosk (it has its own auto-logout flow) and the auth-flow pages
      // (login/logout/registration/password-reset), where a "session expired"
      // prompt is meaningless — the user is already signing in. Match exact
      // path or path + "/" so "/register" doesn't swallow "/registercentre".
      if (isKiosk()) return;
      const AUTH_FLOW_PATHS = [
        "/kiosk",
        "/login",
        "/loginwithusername",
        "/loginwithmobile",
        "/logout",
        "/register",
        "/parent-register",
        "/alumni-register",
        "/forgetpwd",
        "/forgetusername",
      ];
      if (
        AUTH_FLOW_PATHS.some(
          (p) => pathname === p || pathname.startsWith(p + "/")
        )
      ) {
        return;
      }

      // Public pre-login pages (atlas, products, voila, …) are usable while
      // logged out, so there the modal is dismissible — but it DOES appear:
      // the event only fires when a token was present, i.e. a real session
      // expired mid-use, and silently-empty content is worse than a prompt.
      // Once dismissed, don't nag again on public pages; a later 401 on an
      // authenticated page still brings the modal back (non-dismissible).
      const isPublic = isPublicPreloginPath(pathname);
      if (isPublic && dismissedRef.current) return;

      returnPathRef.current = currentPath;
      showRef.current = true;
      setDismissible(isPublic);
      setShow(true);
    };

    window.addEventListener("ilearn:session-expired", handler);
    return () =>
      window.removeEventListener("ilearn:session-expired", handler);
  }, []);

  const handleLogin = () => {
    setShow(false);
    showRef.current = false;
    // Route through /logout so it handles the full localStorage cleanup
    // (user/tenant/enquiryId/notification tokens/DPDP consents). /logout reads
    // loginRedirectTo and forwards it to /login as state.redirectTo, which
    // Login.jsx already honors after successful authentication.
    // Atlas users can't password-login (their accounts get random generated
    // passwords) — send them to the Atlas OTP form instead.
    const isAtlasPath = returnPathRef.current.startsWith("/atlas");
    history.push("/logout", {
      redirectRoute: isAtlasPath ? "/atlas/register" : "/loginwithusername",
      loginRedirectTo: returnPathRef.current,
    });
  };

  const handleDismiss = () => {
    setShow(false);
    showRef.current = false;
    dismissedRef.current = true;
  };

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title>Session Expired</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Your session has expired. Please log in again to continue.
      </Modal.Body>
      <Modal.Footer>
        {dismissible && (
          <Button variant="secondary" onClick={handleDismiss}>
            Continue browsing
          </Button>
        )}
        <Button variant="primary" onClick={handleLogin}>
          Log in
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionExpiredModal;
